import { makeAutoObservable, flow } from 'mobx'

import * as srv from 'admin/services'
import handleError from 'admin/services/error-handler'

const METRIC_VALUE_MAP = {
  revenue: 'totalOrderAmount',
  orders: 'orderCount',
  avgOrder: 'averageOrderAmount',
  itemCount: 'invoiceItemCount',
  margin: 'estimatedMargin',
}

const buildTrendChartModel = (trendResponse, metric = 'revenue', topN = 8) => {
  if (!trendResponse?.series?.length || !trendResponse?.periods?.length) {
    return {
      chartRows: [],
      chartSeries: [],
      hiddenSeriesCount: 0,
    }
  }

  const metricKey = METRIC_VALUE_MAP[metric] || METRIC_VALUE_MAP.revenue
  const rankedSeries = [...trendResponse.series].sort((a, b) => {
    const aValue = a.points.reduce((sum, point) => sum + Number(point[metricKey] || 0), 0)
    const bValue = b.points.reduce((sum, point) => sum + Number(point[metricKey] || 0), 0)
    return bValue - aValue
  })

  const visibleSeries = rankedSeries.slice(0, topN)
  const overflowSeries = rankedSeries.slice(topN)
  const chartSeries = visibleSeries.map((item, index) => {
    return {
      ...item,
      dataKey: `series_${index}`,
    }
  })

  if (overflowSeries.length > 0) {
    const mergedPoints = trendResponse.periods
      .map((period) => {
        return overflowSeries.reduce(
          (acc, series) => {
            const point = series.points.find((seriesPoint) => seriesPoint.period === period)
            acc.totalOrderAmount += Number(point?.totalOrderAmount || 0)
            acc.orderCount += Number(point?.orderCount || 0)
            acc.invoiceItemCount += Number(point?.invoiceItemCount || 0)
            acc.estimatedMargin += Number(point?.estimatedMargin || 0)
            return acc
          },
          {
            period,
            totalOrderAmount: 0,
            orderCount: 0,
            invoiceItemCount: 0,
            estimatedMargin: 0,
          },
        )
      })
      .map((point) => {
        return {
          ...point,
          averageOrderAmount: point.orderCount > 0 ? point.totalOrderAmount / point.orderCount : 0,
        }
      })

    chartSeries.push({
      id: '__others__',
      name: `Others (${overflowSeries.length})`,
      dataKey: 'series_others',
      points: mergedPoints,
    })
  }

  const chartRows = trendResponse.periods.map((period) => {
    return chartSeries.reduce(
      (acc, series) => {
        const point = series.points.find((seriesPoint) => seriesPoint.period === period)
        acc.period = period
        acc[series.dataKey] = Number(point?.[metricKey] || 0)
        return acc
      },
      { period },
    )
  })

  return {
    chartRows,
    chartSeries,
    hiddenSeriesCount: overflowSeries.length,
  }
}

export default class ReportStore {
  loading = false
  loadingTrend = false
  processing = false
  list = []
  total = 0
  driverRouteRows = []
  driverRouteGroups = []
  driverRouteAggregated = []
  driverRouteRepeatMetrics = null
  driverRouteReconciliation = null
  driverRouteTotal = 0
  driverRouteLoading = false
  driverRouteCache = new Map()
  query = {
    name: '',
    sortBy: 'updatedAt',
    sortDirection: 'desc',
  }
  currentPage = 0
  rowsPerPage = 20
  driverRouteQuery = {
    startDate: null,
    endDate: null,
    driverId: null,
    weekday: '',
    jobType: 'all',
    mode: 'raw',
    search: '',
    sortBy: 'date',
    sortDirection: 'asc',
    visualizationMode: 'all',
    selectedDate: '',
  }
  driverRouteCurrentPage = 0
  driverRouteRowsPerPage = 20
  driverRouteHeatmapEnabled = false

  constructor(rootStore) {
    makeAutoObservable(this, {
      fetch: flow,
      fetchTotalSales: flow,
      fetchAverageOrder: flow,
      fetchInvoiceAnalytics: flow,
      fetchDriverRouteReport: flow,
      fetchDriverRouteReportAll: flow,
    })
    this.rootStore = rootStore
  }

  buildTrendChartModel = buildTrendChartModel

  updateQuery = (key, value, resetPage = false) => {
    this.query[key] = value
    if (resetPage) this.currentPage = 0
  }

  resetQuery = () => {
    this.query = {
      name: '',
    }
  }

  updatePage = (page) => {
    this.currentPage = page
  }

  updateRowsPerPage = (perPage) => {
    this.rowsPerPage = perPage
  }

  get queryParams() {
    const params = {
      sortBy: this.query.sortBy,
      sortDirection: this.query.sortDirection,
      limit: this.rowsPerPage,
      offset: this.currentPage * this.rowsPerPage,
    }
    if (this.query.name) params.name = this.query.name
    if (this.query.startDate) params.startDate = this.query.startDate
    if (this.query.endDate) params.endDate = this.query.endDate
    return params
  }

  *fetch(params) {
    try {
      this.loading = true
      const response = yield srv.fetchReportCustomer({
        ...this.queryParams,
        ...params,
      })
      if (response.ok) {
        this.list = response.data.list
        this.total = response.data.total
        return response.data
      }
      throw handleError(response)
    } catch (error) {
      return null
    } finally {
      this.loading = false
    }
  }

  *fetchTotalSales(params) {
    try {
      this.loading = true
      const response = yield srv.fetchTotalSales({
        ...this.queryParams,
        ...params,
      })
      if (!response.ok) throw handleError(response)
      return response.data
    } catch (error) {
      return null
    } finally {
      this.loading = false
    }
  }

  *fetchInvoiceAnalytics(params) {
    const isTrendMode = (params?.mode || '').toLowerCase() === 'trend'

    try {
      if (isTrendMode) {
        this.loadingTrend = true
      } else {
        this.loading = true
      }

      const response = yield srv.fetchInvoiceAnalytics({
        ...this.queryParams,
        ...params,
      })
      if (!response.ok) throw handleError(response)
      return response.data
    } catch (error) {
      return null
    } finally {
      if (isTrendMode) {
        this.loadingTrend = false
      } else {
        this.loading = false
      }
    }
  }

  *fetchAverageOrder(params) {
    try {
      this.loading = true
      const response = yield srv.fetchAverageOrders({
        ...this.queryParams,
        ...params,
      })
      if (!response.ok) throw handleError(response)
      return response.data
    } catch (error) {
      return null
    } finally {
      this.loading = false
    }
  }

  updateDriverRouteFilter = (key, value, resetPage = false) => {
    this.driverRouteQuery[key] = value
    if (resetPage) this.driverRouteCurrentPage = 0
  }

  resetDriverRouteFilters = () => {
    this.driverRouteQuery = {
      startDate: null,
      endDate: null,
      driverId: null,
      weekday: '',
      jobType: 'all',
      mode: 'raw',
      search: '',
      sortBy: 'date',
      sortDirection: 'asc',
      visualizationMode: 'all',
      selectedDate: '',
    }
    this.driverRouteCurrentPage = 0
    this.driverRouteHeatmapEnabled = false
  }

  setDriverRouteHeatmapEnabled = (value) => {
    this.driverRouteHeatmapEnabled = Boolean(value)
  }

  updateDriverRoutePage = (page) => {
    this.driverRouteCurrentPage = page
  }

  updateDriverRouteRowsPerPage = (perPage) => {
    this.driverRouteRowsPerPage = perPage
    this.driverRouteCurrentPage = 0
  }

  clearDriverRouteReport = () => {
    this.driverRouteRows = []
    this.driverRouteGroups = []
    this.driverRouteAggregated = []
    this.driverRouteRepeatMetrics = null
    this.driverRouteReconciliation = null
    this.driverRouteTotal = 0
  }

  get driverRouteFilteredRows() {
    const selectedDate = `${this.driverRouteQuery.selectedDate || ''}`.trim()
    if (!selectedDate) return this.driverRouteRows
    return (this.driverRouteRows || []).filter((row) => row?.date === selectedDate)
  }

  get driverRouteQueryParams() {
    const params = {
      limit: this.driverRouteRowsPerPage,
      offset: this.driverRouteCurrentPage * this.driverRouteRowsPerPage,
      sortBy: this.driverRouteQuery.sortBy,
      sortDirection: this.driverRouteQuery.sortDirection,
    }

    if (this.driverRouteQuery.startDate) params.startDate = this.driverRouteQuery.startDate
    if (this.driverRouteQuery.endDate) params.endDate = this.driverRouteQuery.endDate
    if (this.driverRouteQuery.driverId) params.driverId = this.driverRouteQuery.driverId
    if (this.driverRouteQuery.weekday) params.weekday = this.driverRouteQuery.weekday
    if (this.driverRouteQuery.jobType) params.jobType = this.driverRouteQuery.jobType
    if (this.driverRouteQuery.mode) params.mode = this.driverRouteQuery.mode
    if (this.driverRouteQuery.search) params.search = this.driverRouteQuery.search

    return params
  }

  *fetchDriverRouteReport(params) {
    try {
      this.driverRouteLoading = true
      const mergedParams = {
        ...this.driverRouteQueryParams,
        ...params,
      }
      const cacheKey = JSON.stringify(mergedParams)
      if (this.driverRouteCache.has(cacheKey)) {
        const cached = this.driverRouteCache.get(cacheKey)
        this.driverRouteRows = cached.rows || []
        this.driverRouteGroups = cached.groups || []
        this.driverRouteAggregated = cached.aggregated || []
        this.driverRouteRepeatMetrics = cached.repeatMetrics || null
        this.driverRouteReconciliation = cached.reconciliation || null
        this.driverRouteTotal = cached.total || 0
        return cached
      }
      const response = yield srv.fetchDriverRouteAnalysis(mergedParams)

      if (!response.ok) throw handleError(response)

      this.driverRouteRows = response.data?.rows || response.data?.list || []
      this.driverRouteGroups = response.data?.groups || []
      this.driverRouteAggregated = response.data?.aggregated || []
      this.driverRouteRepeatMetrics = response.data?.repeatMetrics || null
      this.driverRouteReconciliation = response.data?.reconciliation || null
      this.driverRouteTotal = response.data?.total || 0
      this.driverRouteCache.set(cacheKey, response.data || {})
      return response.data
    } catch (error) {
      this.clearDriverRouteReport()
      this.rootStore.notificationStore.setError(
        error?.message || 'Failed to fetch driver route analysis.',
      )
      return null
    } finally {
      this.driverRouteLoading = false
    }
  }

  *fetchDriverRouteReportAll(params) {
    try {
      this.driverRouteLoading = true
      const response = yield srv.fetchDriverRouteAnalysis({
        ...this.driverRouteQueryParams,
        limit: -1,
        offset: 0,
        ...params,
      })

      if (!response.ok) throw handleError(response)
      return response.data?.list || []
    } catch (error) {
      return []
    } finally {
      this.driverRouteLoading = false
    }
  }
}
